/**
 * Main App Component
 * GIBS Guru 2.0 - Chat Interface
 */

import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Container,
  Paper,
  Box,
  Typography,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Fab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Logout as LogoutIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';

import ChatContainer from './components/ChatContainer';
import MessageInput from './components/MessageInput';
import StatusBar from './components/StatusBar';
import Login from './components/Login';
import { AuthProvider, useAuth } from './context/AuthContext';
import agentAPI from './services/api';
import StreamingBuffer from './utils/streamingBuffer';

// Create a theme for the app
const theme = createTheme({
  palette: {
    primary: {
      main: '#2C365E',
    },
    secondary: {
      main: '#FFD639',
    },
    background: {
      default: '#e0dedeff',
    },
  },
  typography: {
    fontFamily: '"Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

// Main app content component (requires authentication)
function MainApp() {
  const { logout, isEmbedded } = useAuth();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [agentStatus, setAgentStatus] = useState('checking');
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Ref for scrolling to bottom
  const messagesEndRef = useRef(null);
  // Ref for the chat container to detect scroll position
  const chatContainerRef = useRef(null);

  // isThinking is now driven by server-emitted thinking_start / thinking_end chunks

  // Create streaming buffer instance with performance-optimized settings
  const streamingBuffer = useRef(new StreamingBuffer({
    charsPerRelease: 5,      // Typewriter drip: ~5 chars per tick
    releaseInterval: 30,     // ~33 fps for smooth animation
    flushDelay: 50
  }));

  // Track whether a tool is currently executing. Used to bypass the typewriter
  // buffer for tokens that arrive out-of-order during tool execution (a side effect
  // of LangGraph interleaving "messages" and "custom" stream channels).
  const toolActiveRef = useRef(false);

  // Initialize app
  useEffect(() => {
    checkAgentHealth();
    setSessionId(agentAPI.sessionId);
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect scroll position to show/hide scroll to top button
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      // Show button if scrolled more than 300px from top
      const scrolledDown = chatContainer.scrollTop > 300;
      setShowScrollTop(scrolledDown);
    };

    chatContainer.addEventListener('scroll', handleScroll);
    return () => chatContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => {
    chatContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Check if the agent is healthy
   */
  const checkAgentHealth = async () => {
    try {
      const health = await agentAPI.checkHealth();
      setAgentStatus(health.agent_status || 'operational');
    } catch (error) {
      console.error('Health check failed:', error);
      setAgentStatus('error');
    }
  };

  /**
   * Handle sending a message with streaming
   */
  const handleSendMessage = async (message) => {
    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);
    setStatus('Working...');

    // Create assistant message with streaming flag
    const assistantMessageId = Date.now() + 1;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      thinkingContent: '',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      artifacts: {},
    };

    // Add empty assistant message that will be updated
    setMessages(prev => [...prev, assistantMessage]);

    // Start the streaming buffer for this message
    streamingBuffer.current.start(assistantMessageId, (msgId, text) => {
      // Callback to handle released text from buffer
      setMessages(prev => prev.map(msg =>
        msg.id === msgId
          ? { ...msg, content: msg.content + text }
          : msg
      ));
    });

    try {
      // Stream the response
      await agentAPI.streamMessage(message, (chunk) => {
        // Handle different chunk types
        switch (chunk.type) {
          case 'text':
          case 'token':
          case 'message':
            if (chunk.content) {
              // Clear status once actual text starts arriving
              if (!toolActiveRef.current) {
                setStatus('');
              }
              if (toolActiveRef.current) {
                // A tool is executing — LangGraph may deliver these tokens out-of-order
                // on the messages channel after the custom tool_start already fired.
                // Release immediately so they don't pile up and burst when the tool ends.
                streamingBuffer.current.flushImmediate(chunk.content);
              } else {
                streamingBuffer.current.addToBuffer(chunk.content);
              }
            }
            break;

          case 'thinking_start':
            setIsThinking(true);
            break;

          case 'thinking_end':
            setIsThinking(false);
            break;

          case 'thinking_token':
            if (chunk.content) {
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, thinkingContent: msg.thinkingContent + chunk.content }
                  : msg
              ));
            }
            break;

          case 'status':
            // Hard stream boundary — flush all buffered chars immediately before tool runs
            streamingBuffer.current.flushImmediate();
            if (chunk.status_message) {
              setStatus(chunk.status_message);
            } else if (chunk.tool) {
              setStatus(`Using tool: ${chunk.tool}...`);
            }
            break;

          case 'tool_start':
            // Hard boundary + add pending tool call card to message
            streamingBuffer.current.flushImmediate();
            toolActiveRef.current = true;
            if (chunk.status_message) setStatus(chunk.status_message);
            setMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? { ...msg, toolCalls: [...(msg.toolCalls || []), { tool: chunk.tool, status: 'pending', args: chunk.args }] }
                : msg
            ));
            break;

          case 'tool_result':
            // Resolve the pending card for this tool
            toolActiveRef.current = false;
            streamingBuffer.current.flushImmediate();
            if (chunk.status_message) setStatus(chunk.status_message);
            setMessages(prev => prev.map(msg => {
              if (msg.id !== assistantMessageId) return msg;
              const toolCalls = [...(msg.toolCalls || [])];
              // Find the last pending entry for this tool and resolve it
              let resolved = false;
              for (let i = toolCalls.length - 1; i >= 0; i--) {
                if (toolCalls[i].tool === chunk.tool && toolCalls[i].status === 'pending') {
                  toolCalls[i] = {
                    ...toolCalls[i],
                    status: chunk.success === false ? 'error' : 'done',
                    summary: chunk.summary,
                    args: chunk.args,
                  };
                  resolved = true;
                  break;
                }
              }
              // Fallback: if no pending entry found (tool_start was missed), append a resolved one
              if (!resolved) {
                toolCalls.push({
                  tool: chunk.tool,
                  status: chunk.success === false ? 'error' : 'done',
                  summary: chunk.summary,
                  args: chunk.args,
                });
              }
              return { ...msg, toolCalls };
            }));
            break;
            
          case 'artifact':
            // Add artifact to message
            setMessages(prev => prev.map(msg => 
              msg.id === assistantMessageId 
                ? { 
                    ...msg, 
                    artifacts: { 
                      ...msg.artifacts, 
                      [chunk.artifact_type]: chunk.artifact_data 
                    } 
                  }
                : msg
            ));
            break;
            
          case 'complete':
            // Hard stream boundary — flush all buffered chars immediately, then schedule cleanup
            setIsThinking(false);
            streamingBuffer.current.flushImmediate();
            streamingBuffer.current.complete();

            // After a small delay, remove streaming flag and add artifacts
            setTimeout(() => {
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? {
                      ...msg,
                      isStreaming: false,
                      artifacts: chunk.artifacts || msg.artifacts
                    }
                  : msg
              ));
            }, 80); // Give buffer time to flush
            setStatus('');
            break;

          case 'error':
            toolActiveRef.current = false;
            setIsThinking(false);
            streamingBuffer.current.flushImmediate();
            streamingBuffer.current.stop();

            // Handle error in stream
            setError(`Stream error: ${chunk.content || 'Unknown error'}`);
            setMessages(prev => prev.map(msg =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: msg.content || chunk.content || 'An error occurred while processing your request.',
                    isStreaming: false,
                    isError: true
                  }
                : msg
            ));
            break;
            
          default:
            console.log('Unknown chunk type:', chunk);
        }
      });
      
    } catch (error) {
      console.error('Error streaming message:', error);
      setError(error.message || 'Failed to stream message');

      // Stop the streaming buffer
      streamingBuffer.current.stop();

      // Update the assistant message with error
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMessageId
          ? {
              ...msg,
              content: msg.content || 'I apologize, but I encountered an error processing your request. Please try again.',
              isStreaming: false,
              isError: true
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
      setStatus('');
      // Ensure buffer is stopped
      if (streamingBuffer.current.isStreaming) {
        streamingBuffer.current.stop();
      }
    }
  };

  /**
   * Reset the conversation
   */
  const handleReset = () => {
    // Skip confirmation if embedded (no prompt needed since UI is embedded)
    if (isEmbedded || window.confirm('Start a new conversation? Current conversation will be lost.')) {
      // Stop any active streaming
      streamingBuffer.current.stop();

      setMessages([]);
      const newSessionId = agentAPI.resetSession();
      setSessionId(newSessionId);
      setError(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        flexGrow: 1,
        height: isEmbedded ? 'auto' : '100vh',
        minHeight: isEmbedded ? '100vh' : 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* App Bar */}
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <AccountBalanceIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              GIBS Guru 2.0
            </Typography>
            <Chip 
              label={`Agent: ${agentStatus}`}
              color={agentStatus === 'operational' ? 'success' : 'error'}
              size="small"
              sx={{ mr: 2 }}
            />
            <Chip 
              label={`Session: ${sessionId.slice(-8)}`}
              variant="outlined"
              size="small"
              sx={{ mr: 2, color: 'white', borderColor: 'white' }}
            />
            <IconButton color="inherit" onClick={handleReset} title="New Conversation">
              <RefreshIcon />
            </IconButton>
            {!isEmbedded && (
              <IconButton color="inherit" onClick={logout} title="Logout" sx={{ ml: 1 }}>
                <LogoutIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ flexGrow: 1, py: 3, display: 'flex', flexDirection: 'column' }}>
          <Paper elevation={3} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Chat Messages */}
            <ChatContainer
              ref={chatContainerRef}
              messages={messages}
              onSendMessage={handleSendMessage}
            />
            
            {/* Status Bar */}
            {status && <StatusBar status={status} />}
            
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />

            {/* Message Input */}
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={isLoading || agentStatus !== 'operational'}
              placeholder={
                agentStatus !== 'operational'
                  ? 'Agent is not available...'
                  : 'Ask me about GIBS...'
              }
            />
          </Paper>

          {/* Scroll to Top Button */}
          {showScrollTop && (
            <Fab
              color="primary"
              size="small"
              onClick={scrollToTop}
              sx={{
                position: 'fixed',
                bottom: 100,
                right: 32,
                zIndex: 1000,
                transition: 'opacity 0.3s',
              }}
              aria-label="scroll to top"
            >
              <KeyboardArrowUpIcon />
            </Fab>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

// App authentication wrapper
function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

// Main app with authentication logic
function AuthenticatedApp() {
  const { isAuthenticated, isLoading, login } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e0dedeff',
          }}
        >
          <CircularProgress size={60} sx={{ color: '#2C365E' }} />
        </Box>
      </ThemeProvider>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={login} />
      </ThemeProvider>
    );
  }

  // Show main app if authenticated
  return <MainApp />;
}

export default App;